import { inject, Injectable } from '@angular/core';
import { StripeApi } from '../../api/stripe.api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CartActions } from './cart.actions';
import { map, mergeMap, Observable, of, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';
import { selectProductById } from 'app/store/products/products.selectors';
import { PaymentIntent } from '@stripe/stripe-js';
import { selectActiveSubscription } from '../subscriptions/subscriptions.selectors';
import { KinoSubscription } from '../../models/kino-subscription.model';
import { catchError, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Router } from '@angular/router';
import { ToasterService } from 'app/services/toaster.service';
import { SubscriptionsApiActions } from '../subscriptions/subscriptions.actions';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class CartEffects {
  private actions$: Actions = inject(Actions);
  private stripeApi: StripeApi = inject(StripeApi);
  private store: Store = inject(Store);
  private translateService: TranslateService = inject(TranslateService);
  private router: Router = inject(Router);
  private toastService: ToasterService = inject(ToasterService);
  private destroy$: Subject<void> = new Subject<void>();

  clearCart$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CartActions.clearCart),
      mergeMap(() => {
        this.destroy$.next();
        return of(CartActions.stopLoading());
      })
    );
  });

  initProductOnCart$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CartActions.addProduct),
      mergeMap(({ productId }) => {
        this.store.dispatch(CartActions.startLoading());
        return this.store
          .select(selectProductById(productId))
          .pipe(takeUntil(this.destroy$))
          .pipe(
            switchMap((productData) => {
              const { product, productType } = productData;
              return this.store
                .select(selectActiveSubscription)
                .pipe(takeUntil(this.destroy$))
                .pipe(
                  switchMap((activeSub: KinoSubscription) => {
                    let paymentIntent$: Observable<PaymentIntent | null> =
                      of(null);
                    if (
                      productType === 'subscription' &&
                      product.name !== 'lifetime' &&
                      activeSub &&
                      activeSub.plan.name !== 'prepaidCard'
                    ) {
                      this.store.dispatch(
                        CartActions.setIsMigration({ isMigration: true })
                      );
                      paymentIntent$ = this.stripeApi.initMigration$(productId);
                    } else {
                      switch (productType) {
                        case 'gift-card':
                          paymentIntent$ =
                            this.stripeApi.initGiftCardPaymentIntent$(
                              productId
                            );
                          break;
                        default:
                          paymentIntent$ =
                            this.stripeApi.initPaymentIntent$(productId);
                          break;
                      }
                    }


                    return paymentIntent$.pipe(takeUntil(this.destroy$)).pipe(
                      map((paymentIntent: any) => {

                        const actions = [
                          CartActions.setProductType({ productType }),
                          CartActions.setProductData({ product }),
                          CartActions.setPaymentIntent({
                            paymentIntent:
                              paymentIntent?.payment_intent ?? paymentIntent,
                          }),
                        ];
                        // Redirection uniquement si on a reçu un message "OK" sans payment_intent
                        if (
                          paymentIntent?.message === 'OK' &&
                          !paymentIntent?.payment_intent
                        ) {
                          this.router.navigate([
                            '/dashboard/subscriptions/successful-payment',
                          ]);
                        }

                        return actions;
                      }),
                      catchError((err) => {
                        if (err?.status === 422) {
                          this.store.dispatch(
                            SubscriptionsApiActions.fetchActiveSubscription()
                          );
                          this.router.navigate(['/dashboard/subscriptions']);
                          this.toastService.warning(
                            this.translateService.instant(
                              'dashboard.subscription.info.subscription'
                            ),
                            this.translateService.instant(
                              'dashboard.subscription.titles.alreadySubscribed'
                            )
                          );
                        }
                        return of([]);
                      })
                    );
                  })
                );
            }),
            mergeMap((actions) => [...actions, CartActions.stopLoading()])
          );
      })
    );
  });
}
